import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, inject, isDevMode, PLATFORM_ID } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { environment } from '@environment/environment';

import { PageType, PageViewTag } from '../models';
import { AnalyticsService } from './analytics.service';

/**
 * Page type assumed when a matched route carries no `page_type` in its `data`.
 * Routes SHOULD declare an explicit taxonomy; this only stops an unannotated
 * route from silently emitting an invalid (typeless) `page_view`.
 */
const FALLBACK_PAGE_TYPE: PageType = 'content_page';

/**
 * Page types whose `page_view` is deliberately delayed until article content
 * has loaded, so that a component can `enrich()` it with article-level fields
 * (category/title/slug/id) before the single emission fires.
 */
const ENRICHABLE_PAGE_TYPES = new Set<PageType>(['news_article', 'blog_article']);

/** Macrotask delay used to flush non-article pages once the title is fresh. */
const NEXT_TICK_MS = 0;

/**
 * Safety-net delay for enrichable pages: if `enrich()` never arrives (e.g. the
 * article data load errors), the base `page_view` still fires exactly once.
 */
const ENRICH_FALLBACK_MS = 1200;

/**
 * The SINGLE emitter of `page_view` events for the whole app.
 *
 * Routing analytics are notoriously prone to double-counting: a navigation
 * fires `NavigationEnd`, then a component's data stream (`combineLatest`, a
 * signal `effect`, an HTTP resolve) re-emits one or more times, and each path
 * naively pushes its own `page_view`. To make this impossible, ALL `page_view`
 * emission is funnelled through this one service and gated per navigation id.
 *
 * Contract — exactly one `page_view` per navigation:
 *  - Each `NavigationEnd` carries a monotonic numeric `id`. We track the
 *    current one in `#currentNavId` and the last one we actually pushed in
 *    `#pushedNavId`. The flush is a no-op once `#pushedNavId === #currentNavId`,
 *    so any number of re-emits within the same navigation collapse to one push.
 *  - A new `NavigationEnd` discards any unflushed pending base + timer from the
 *    previous navigation, so a fast navigation never emits a stale page.
 *
 * Timing — the title must be read AFTER Angular's `TitleStrategy` has applied
 * the route `title` (which also happens on `NavigationEnd`). We therefore never
 * read the title eagerly; the push is deferred and `Title.getTitle()` is read
 * inside `#flush()`:
 *  - Non-article pages: schedule a `setTimeout(0)` flush — the title is fresh
 *    on the next macrotask.
 *  - Article pages (`ENRICHABLE_PAGE_TYPES`): stash the base and wait. The page
 *    flushes the instant a component calls `enrich()`, or after
 *    `ENRICH_FALLBACK_MS` as a safety net if enrichment never arrives.
 *
 * Browser-only: nothing subscribes or pushes under SSR.
 */
@Injectable()
export class PageViewService {
  #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  #router = inject(Router);
  #title = inject(Title);
  #document = inject(DOCUMENT);
  #analytics = inject(AnalyticsService);

  /** Set once `init()` has wired the router subscription; makes init idempotent. */
  #started = false;

  /** Navigation id of the in-flight navigation (`NavigationEnd.id`). */
  #currentNavId: number | null = null;

  /** Navigation id whose `page_view` has already been pushed. */
  #pushedNavId: number | null = null;

  /** The page_view payload minus the (deferred, freshly-read) title. */
  #pendingBase: Omit<PageViewTag, 'event'> | null = null;

  /** Handle for the scheduled (or fallback) flush of the current navigation. */
  #flushTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Starts the per-navigation `page_view` pipeline. Browser-only and
   * idempotent: a second call (or any call under SSR) is a no-op.
   */
  init(): void {
    if (!this.#isBrowser || this.#started) return;
    this.#started = true;

    this.#router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.#onNavigationEnd(e));
  }

  /**
   * Contributes article-level fields to the CURRENT navigation's pending
   * `page_view` and flushes it immediately. Article pages defer emission until
   * this is called (see class docs). Browser-only.
   *
   * Re-emit safe: if this navigation has already been pushed (e.g. a
   * `combineLatest`/signal re-fires within the same navigation), the call is a
   * no-op. Undefined values in `partial` are ignored so a missing optional
   * never overwrites an already-set field.
   */
  enrich(
    partial: Partial<Pick<PageViewTag, 'content_category' | 'article_title' | 'article_slug' | 'article_id'>>,
  ): void {
    if (!this.#isBrowser || this.#currentNavId === null) return;

    // Already emitted for this navigation — ignore duplicate enrichment.
    if (this.#pushedNavId === this.#currentNavId) return;

    if (this.#pendingBase !== null) {
      for (const [key, value] of Object.entries(partial)) {
        if (value !== undefined) {
          (this.#pendingBase as Record<string, unknown>)[key] = value;
        }
      }
    }

    // Enrichment has arrived: cancel the safety-net timer and flush now.
    this.#clearTimer();
    this.#flush();
  }

  /**
   * Handles one `NavigationEnd`: discards any unflushed previous navigation,
   * resolves the route taxonomy, builds the page_view base, then either defers
   * a flush (non-article) or waits for `enrich()` (article, with a fallback).
   */
  #onNavigationEnd(e: NavigationEnd): void {
    // New navigation: drop any pending base/timer from the previous one so a
    // fast navigation can never flush a stale page.
    this.#clearTimer();
    this.#pendingBase = null;
    this.#currentNavId = e.id;

    const data = this.#resolveDeepestData();
    let pageType = data['page_type'] as PageType | undefined;
    const section = data['section'] as string | undefined;

    if (pageType === undefined) {
      pageType = FALLBACK_PAGE_TYPE;
      if (isDevMode()) {
        console.warn(`[PageViewService] Route "${e.urlAfterRedirects}" has no page_type; using "${FALLBACK_PAGE_TYPE}".`);
      }
    }

    const pageLocation = this.#document.location?.href ?? e.urlAfterRedirects;

    const base: Omit<PageViewTag, 'event'> = {
      page_location: pageLocation,
      page_path: e.urlAfterRedirects,
      // Read fresh inside #flush() once the TitleStrategy has run.
      page_title: '',
      page_type: pageType,
      environment: environment.name,
    };

    // For listings (and any non-article page) surface `section` as the
    // content category. Article pages get their category via enrich().
    if (section !== undefined && !ENRICHABLE_PAGE_TYPES.has(pageType)) {
      base.content_category = section;
    }

    this.#pendingBase = base;

    if (ENRICHABLE_PAGE_TYPES.has(pageType)) {
      // Wait for enrich(); fall back so the base still fires exactly once.
      this.#flushTimer = setTimeout(() => this.#flush(), ENRICH_FALLBACK_MS);
    } else {
      // Defer to the next macrotask so Title.getTitle() reads the fresh title.
      this.#flushTimer = setTimeout(() => this.#flush(), NEXT_TICK_MS);
    }
  }

  /**
   * Walks `firstChild` from the router state root to the deepest matched
   * snapshot and returns its `data` (route `data` is inherited/merged by the
   * router, so the deepest snapshot carries the effective taxonomy).
   */
  #resolveDeepestData(): { [key: string]: unknown } {
    let snapshot: ActivatedRouteSnapshot = this.#router.routerState.snapshot.root;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return snapshot.data;
  }

  /**
   * Emits the pending `page_view` exactly once for the current navigation.
   * Reads the title fresh, strips undefined optionals, pushes via
   * `AnalyticsService`, then marks the navigation as pushed.
   */
  #flush(): void {
    if (this.#currentNavId === null || this.#pendingBase === null) return;

    // Idempotent guard: never push the same navigation twice.
    if (this.#pushedNavId === this.#currentNavId) return;

    const pageTitle = this.#title.getTitle();

    const tag: PageViewTag = {
      event: 'page_view',
      ...this.#pendingBase,
      page_title: pageTitle,
    };

    // Omit any optional field that is undefined (never emit `key: undefined`).
    for (const key of Object.keys(tag) as (keyof PageViewTag)[]) {
      if (tag[key] === undefined) {
        delete tag[key];
      }
    }

    this.#analytics.push(tag);

    this.#pushedNavId = this.#currentNavId;
    this.#pendingBase = null;
    this.#clearTimer();
  }

  /** Cancels and clears any scheduled flush timer. */
  #clearTimer(): void {
    if (this.#flushTimer !== null) {
      clearTimeout(this.#flushTimer);
      this.#flushTimer = null;
    }
  }
}
